import { InstallmentFieldError } from 'components/common/balance-adjustment/installmentHelpers';
import type {
  APIAnswer,
  EditableQuestions,
  FormAnswer,
  FormQuestion,
} from 'components/registration/v2/PostRegEditDrawer';
import {
  Installment,
  InstallmentEditable,
  InstallmentPaymentMethod,
  LineItem,
} from 'components/registration/v2/types';

// Defines the entire structure of the post reg edit drawer's form.
export type PostRegFormStructure = {
  feeRelatedQuestions: FormQuestion[];
  additionalQuestions: FormQuestion[];
};

// Defines the shape of the data that is stored by the form.
export interface PostRegEditFormState {
  [questionId: string]: FormAnswer[];
}

export enum LoadingType {
  GetEditableQuestions,
  GetPreviewLineItems,
  SubmitEdits,
  UploadFile,
}

export enum FormPage {
  Editing,
  Reviewing,
  Confirmation,
}
export interface PreviewLineItems {
  newLineItems: LineItem[];
  canceledLineItems: LineItem[];
  currentBalance: number;
  newBalance: number;
  lineItemTotal: number;
  installments: Installment[]; // origingal values from api
  registrationsInOrder: number;
}

export interface Confirmation {
  order: {
    id: number;
    orderNumber: string;
    scopeId: number;
    scopeType: string;
  };
  orderUpdated: boolean;
  installmentsUpdated: boolean;
  additionalQuestionsUpdated?: boolean;
  participantGroupUpdated?: boolean;
}

export interface PendingEdit {
  questionId: string;
  answers: APIAnswer[];
}
// If value exists but details does not, the error is for the value field
// If details exists, the error is for the details field for the given value
// If both value and details are undefined, the error is for the question as a whole
export type FormError = {
  value?: string;
  details?: string;
  error: string;
};

export interface PendingPayment {
  orderId?: number;
  installments?: InstallmentEditable[]; // edited values to send back to api
  savedPaymentMethod?: InstallmentPaymentMethod;
  paidBalance?: number;
  error?: string;
  fieldErrors?: Record<string, InstallmentFieldError>;
  installmentsUpdated?: boolean;
}

export interface PostRegDrawerState {
  loading: {
    [LoadingType.GetEditableQuestions]: boolean;
    [LoadingType.GetPreviewLineItems]: boolean;
    [LoadingType.SubmitEdits]: boolean;
    [LoadingType.UploadFile]: boolean;
    error?: string;
  };
  formResultId: number;
  orderId: number;
  registrantInfo: { fullName: string; email: string };
  formStructure: PostRegFormStructure;
  questions: EditableQuestions;
  currentFormState: PostRegEditFormState;
  dirtyFields: string[]; // IDs of questions that have been changed
  // Stores the error message for a given value or details field
  formErrors: {
    [questionId: string]: FormError[];
  };
  formPage: FormPage;
  isFormValid: boolean;
  pendingEdits: PendingEdit[];
  previewLineItems?: PreviewLineItems;
  pendingPayment?: PendingPayment;
  confirmation?: Confirmation;
}

export enum ActionType {
  SetLoading,
  SetFormResultId,
  HydrateForm,
  ResetState,
  UpdateFormField,
  UpdateFormFieldDetails,
  UpdateFormFieldFile,
  setFormPage,
  SetPreviewLineItems,
  SetPendingEdits,
  SetPendingPayment,
  SetConfirmation,
}

export type HydrateFormPayload = {
  questions: EditableQuestions;
};

export type SetFormStatePayload = {
  formState: PostRegEditFormState;
};

export type SetDirtyFieldsPayload = {
  dirtyFields: string[];
};

export type SetFormErrorsPayload = {
  formErrors: { [questionId: string]: FormError[] };
};

export type UpdateFormErrorPayload = {
  questionId: string;
  errors: FormError[];
};

export type SetFormPagePayload = {
  formPage: FormPage;
};

export type UpdateFormFieldDetailsPayload = {
  questionId: string;
  value: string;
  details: string;
};

export type SetFormResultIdPayload = {
  formResultId: number;
  orderId: number;
};

export type SetPendingPaymentPayload = {
  pendingPayment: PendingPayment;
};

export type UpdateFormFieldPayload = {
  questionId: string;
  values: string[];
};

export type UpdateFormFieldFilePayload = {
  questionId: string;
  value: string;
  file?: File;
};

export type SetPreviewLineItemsPayload = {
  previewLineItems: PreviewLineItems;
};

export type SetConfirmationPayload = {
  confirmation: Confirmation;
};

export type SetPendingEditsPayload = {
  pendingEdits: PendingEdit[];
};
